import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, FileText } from 'lucide-react';
import { useRef, useState, type FocusEvent, type ReactNode } from 'react';
import type { MarkingAnswerView, MarkingAttemptView, MarkingQueueRow } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Chip } from '../../../components/Chip';
import { Field } from '../../../components/Field';
import { Input, Textarea } from '../../../components/Input';
import { SkeletonLines } from '../../../components/Skeleton';
import { RESULT_TONES } from '../../../components/status-tones';
import { ToneChip } from '../../../components/Tone';
import { ForbiddenState, NotFoundState, ServerErrorState } from '../../../components/system-states/SystemState';
import { fieldErrorMessage } from '../../../lib/api-client';
import { EMPTY, formatDateTime } from '../../../lib/format';
import { useToasts } from '../../../stores/toasts';
import { MARKING_KEY, markAnswer, markingAttemptKey, useMarkingAttempt, useMarkingQueue } from '../api/exam-sessions-api';
import { RESULTS_KEY } from '../api/assessments-api';
import styles from '../components/Assessments.module.css';
import { AssessmentsHeader } from '../components/AssessmentsHeader';

/**
 * ASM-06 — one test's written answers (F9.07), each beside the question's "note for
 * whoever marks it". Every mark moves the running total at once (QA test 4); the last
 * one sets pass or fail, and a pass moves on by itself where the job says so (S18 Q6).
 */
export function MarkingPage({ attemptId }: { readonly attemptId: string }): ReactNode {
  const query = useMarkingAttempt(attemptId);
  const queue = useMarkingQueue();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const show = useToasts((state) => state.show);

  if (query.isPending) return <SkeletonLines count={10} />;
  if (query.error) {
    if (query.error.status === 403) return <ForbiddenState resource="this test" contact="Your admin" />;
    if (query.error.status === 404) return <NotFoundState onGoBack={() => void navigate({ to: '/assessments/marking' })} />;
    return <ServerErrorState onRetry={() => void query.refetch()} />;
  }
  const view = query.data;
  const toMark = view.answers.filter((answer) => answer.needsPerson);
  const byPerson = toMark.filter((answer) => answer.points !== null).length;

  async function save(answer: MarkingAnswerView, points: number, note: string | null): Promise<void> {
    const key = markingAttemptKey(attemptId);
    const snapshot = queryClient.getQueryData<MarkingAttemptView>(key);
    if (snapshot) {
      const answers = snapshot.answers.map((row) => (row.questionId === answer.questionId ? { ...row, points, graderNote: note } : row));
      const scoreRaw = answers.reduce((sum, row) => sum + (row.points ?? 0), 0);
      queryClient.setQueryData<MarkingAttemptView>(key, { ...snapshot, answers, scoreRaw, scorePercent: snapshot.scoreMax === 0 ? 0 : Math.round((scoreRaw / snapshot.scoreMax) * 10_000) / 100 });
    }
    try {
      const updated = await markAnswer(attemptId, answer.questionId, points, note);
      queryClient.setQueryData(key, updated);
      void queryClient.invalidateQueries({ queryKey: [...MARKING_KEY, 'queue'] });
      void queryClient.invalidateQueries({ queryKey: RESULTS_KEY });
      if (updated.isFinished) {
        show({
          tone: 'success',
          message: `Marked — ${updated.scorePercent}%, ${updated.didPass ? 'passed' : 'below the pass mark'}`,
          detail: `${view.candidateName}'s result is on the job's Assessment tab.`,
          action: { label: 'Next to mark', run: () => void navigate({ to: '/assessments/marking' }) },
        });
      }
    } catch (error) {
      queryClient.setQueryData(key, snapshot);
      show({ tone: 'danger', message: "The mark wasn't saved", detail: fieldErrorMessage(error) });
    }
  }

  const verdict = view.isFinished ? (view.didPass ? <ToneChip tone={RESULT_TONES.passed}>Passed</ToneChip> : <ToneChip tone={RESULT_TONES.below}>Below the pass mark</ToneChip>) : <ToneChip tone={RESULT_TONES.marking}>{toMark.length - byPerson} to mark</ToneChip>;

  return (
    <div className={styles.screen}>
      <AssessmentsHeader current="marking" />
      <div className={styles.markingNav}>
        <Link to="/assessments/marking" className={styles.back}>
          <ChevronLeft size={14} aria-hidden />
          Marking
        </Link>
        <QueueSteps attemptId={attemptId} queue={queue.data ?? []} onOpen={(id) => void navigate({ to: '/assessments/marking/$attemptId', params: { attemptId: id } })} />
      </div>
      <Card
        title={`${view.candidateName} · ${view.testName}`}
        meta={`${view.jobTitle} · submitted ${view.submittedAt ? formatDateTime(view.submittedAt) : EMPTY}${view.graderName ? ` · marker ${view.graderName}` : ''}`}
        action={verdict}
      >
        <p className={styles.summary} aria-live="polite">
          Running total <strong>{view.scoreRaw}</strong> of {view.scoreMax} ({view.scorePercent}%) · pass mark {view.passPercent}%
        </p>
        {!view.canMark && !view.isFinished && <p className={styles.notice}>Only {view.graderName ?? 'the assigned marker'} or HR can mark this test.</p>}
      </Card>
      {toMark.map((answer, index) => (
        <AnswerCard key={answer.questionId} number={index + 1} answer={answer} canMark={view.canMark} onSave={(points, note) => void save(answer, points, note)} />
      ))}
      {view.answers.length > toMark.length && (
        <p className={styles.pageHint}>
          {view.answers.length - toMark.length} other answer{view.answers.length - toMark.length === 1 ? ' was' : 's were'} marked by the system ({view.answers
            .filter((answer) => !answer.needsPerson)
            .reduce((sum, answer) => sum + (answer.points ?? 0), 0)}{' '}
          marks).
        </p>
      )}
    </div>
  );
}

/**
 * "x of y" with Previous and Next through the marking queue, in its order (oldest first). Hidden
 * when there is nowhere to go, or when this attempt is not in the queue — a finished one drops out.
 */
export function QueueSteps({ attemptId, queue, onOpen }: { readonly attemptId: string; readonly queue: readonly MarkingQueueRow[]; readonly onOpen: (attemptId: string) => void }): ReactNode {
  const at = queue.findIndex((row) => row.attemptId === attemptId);
  if (at < 0 || queue.length < 2) return null;
  const previous = queue[at - 1];
  const next = queue[at + 1];
  return (
    <span className={styles.markingSteps}>
      <span className={styles.markingPosition}>
        {at + 1} of {queue.length}
      </span>
      <Button size="sm" variant="outline" icon={ChevronLeft} disabled={!previous} onClick={() => previous && onOpen(previous.attemptId)}>
        Previous
      </Button>
      <Button size="sm" variant="outline" disabled={!next} onClick={() => next && onOpen(next.attemptId)}>
        Next
        <ChevronRight size={14} aria-hidden />
      </Button>
    </span>
  );
}

/**
 * The mark and its note save together, once focus leaves the pair. Saving the mark
 * alone on leaving its box finished the test on the last answer and locked out the
 * note (bug #45) — a finishing mark has side effects, so it is never an inline edit
 * of one field (06-qol §3).
 */
export function AnswerCard({ number, answer, canMark, onSave }: { readonly number: number; readonly answer: MarkingAnswerView; readonly canMark: boolean; readonly onSave: (points: number, note: string | null) => void }): ReactNode {
  const [text, setText] = useState(answer.points === null ? '' : String(answer.points));
  const [note, setNote] = useState(answer.graderNote ?? '');
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const value = text.trim() === '' ? null : Number(text);
  const problem = value !== null && (Number.isNaN(value) || value < 0 || value > answer.maxPoints) ? `Between 0 and ${answer.maxPoints}.` : undefined;

  function commit(): void {
    if (value === null || problem) return;
    const rounded = Math.round(value * 100) / 100;
    const trimmed = note.trim() || null;
    if (rounded === answer.points && trimmed === answer.graderNote) return;
    onSave(rounded, trimmed);
  }

  function commitOnLeave(event: FocusEvent<HTMLDivElement>): void {
    if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return;
    commit();
  }

  return (
    <Card title={`${number}. ${answer.sectionName}`} meta={`Worth ${answer.maxPoints} mark${answer.maxPoints === 1 ? '' : 's'}`} action={answer.points === null ? <Chip tone={RESULT_TONES.marking} plain>To mark</Chip> : <Chip tone="success" plain>{answer.points} given</Chip>}>
      <div className={styles.form}>
        <p className={styles.questionText}>{answer.questionText}</p>
        {answer.markingNote && (
          <p className={styles.notice}>
            <strong>Marking note:</strong> {answer.markingNote}
          </p>
        )}
        <div className={styles.answer}>
          {answer.answerText !== null && <p className={styles.answerText}>{answer.answerText}</p>}
          {answer.file && (
            <a href={`/api/v1/documents/${answer.file.documentId}`} target="_blank" rel="noreferrer" className={styles.itemName}>
              <FileText size={15} aria-hidden /> {answer.file.name}
            </a>
          )}
          {answer.answerText === null && !answer.file && <p className={styles.meta}>No answer given.</p>}
        </div>
        {canMark && (
          <div className={styles.markRow} onBlur={commitOnLeave}>
            <Field label="Mark" error={problem}>
              {({ inputId, describedBy, isInvalid }) => (
                <Input
                  id={inputId}
                  aria-describedby={describedBy}
                  isInvalid={isInvalid}
                  isNumeric
                  inputMode="decimal"
                  suffix={`/ ${answer.maxPoints}`}
                  value={text}
                  onChange={(event) => setText(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') noteRef.current?.focus();
                  }}
                />
              )}
            </Field>
            <Field label="Note" isOptional hint="For the recruiter. Never shown to the candidate.">
              {({ inputId, describedBy }) => <Textarea ref={noteRef} id={inputId} aria-describedby={describedBy} rows={1} value={note} onChange={(event) => setNote(event.target.value)} />}
            </Field>
          </div>
        )}
      </div>
    </Card>
  );
}
